import React, { useState, useEffect, useCallback } from 'react';
import { downloadICS } from './generateICS';
import { publicFetch } from '../utils/publicApi';
import { openBlankCoachTab, openCoachMode } from '../swishiq/deviceStore';

const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

export default function PublicMyRoster({ token, weekOffset }) {
  const [games, setGames] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [courtAssignments, setCourtAssignments] = useState([]);
  // Swish IQ trial days this person coaches (unpaid, no accept/reject).
  const [trials, setTrials] = useState([]);
  const [launchingId, setLaunchingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Per-type metadata
  const [gameMeta, setGameMeta] = useState({ cutoffHours: 24, responseEnabled: true, totalPay: 0, attendedPay: 0 });
  const [coachMeta, setCoachMeta] = useState({ cutoffHours: 24, responseEnabled: true, totalPay: 0, attendedPay: 0 });
  const [courtMeta, setCourtMeta] = useState({ cutoffHours: 24, responseEnabled: true, totalPay: 0, attendedPay: 0 });
  const [payVisible, setPayVisible] = useState(true);

  const formatLocalDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const getDateRange = useCallback(() => {
    const today = new Date();
    const day = today.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diffToMonday + (weekOffset * 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return {
      startDate: formatLocalDate(monday),
      endDate: formatLocalDate(sunday)
    };
  }, [weekOffset]);

  // Load all three rosters in parallel
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      setError('');
      const { startDate, endDate } = getDateRange();

      const results = await Promise.allSettled([
        publicFetch(`${API_BASE}/public/roster/games/${token}?startDate=${startDate}&endDate=${endDate}`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
        publicFetch(`${API_BASE}/public/roster/coaching/${token}?startDate=${startDate}&endDate=${endDate}`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
        publicFetch(`${API_BASE}/public/roster/court/${token}?startDate=${startDate}&endDate=${endDate}`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
        publicFetch(`${API_BASE}/public/swishiq/portal/${token}/sessions?startDate=${startDate}&endDate=${endDate}`, { credentials: 'include' }).then(r => r.ok ? r.json() : null)
      ]);

      // Games
      const gameData = results[0].status === 'fulfilled' ? results[0].value : null;
      if (gameData) {
        setGames(gameData.games || []);
        if (gameData.payVisible !== undefined) setPayVisible(gameData.payVisible);
        setGameMeta({
          cutoffHours: gameData.cutoffHours ?? 24,
          responseEnabled: gameData.responseEnabled ?? true,
          totalPay: gameData.totalPay || 0,
          attendedPay: gameData.attendedPay || 0
        });
      } else {
        setGames([]);
        setGameMeta(prev => ({ ...prev, totalPay: 0, attendedPay: 0 }));
      }

      // Coaching
      const coachData = results[1].status === 'fulfilled' ? results[1].value : null;
      if (coachData) {
        setPrograms(coachData.programs || []);
        setCoachMeta({
          cutoffHours: coachData.cutoffHours ?? 24,
          responseEnabled: coachData.responseEnabled ?? true,
          totalPay: coachData.totalPay || 0,
          attendedPay: coachData.attendedPay || 0
        });
      } else {
        setPrograms([]);
        setCoachMeta(prev => ({ ...prev, totalPay: 0, attendedPay: 0 }));
      }

      // Court
      const courtData = results[2].status === 'fulfilled' ? results[2].value : null;
      if (courtData) {
        setCourtAssignments(courtData.assignments || []);
        setCourtMeta({
          cutoffHours: courtData.cutoffHours ?? 24,
          responseEnabled: courtData.responseEnabled ?? true,
          totalPay: courtData.totalPay || 0,
          attendedPay: courtData.attendedPay || 0
        });
      } else {
        setCourtAssignments([]);
        setCourtMeta(prev => ({ ...prev, totalPay: 0, attendedPay: 0 }));
      }

      // Swish IQ trials
      const trialData = results[3].status === 'fulfilled' ? results[3].value : null;
      setTrials(Array.isArray(trialData) ? trialData.filter(s => s.date >= startDate && s.date <= endDate) : []);

      setLoading(false);
    };

    loadAll();
  }, [token, weekOffset, getDateRange]);

  // ==================== FORMATTING ====================

  const formatLongDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-AU', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return `${h12}:${m} ${ampm}`;
  };

  // ==================== CUTOFF / RESPOND LOGIC ====================

  const isWithinCutoffGame = (game) => {
    if (!game.scheduledTime) return false;
    const gameTime = new Date(game.scheduledTime);
    const cutoffTime = new Date(gameTime.getTime() - gameMeta.cutoffHours * 60 * 60 * 1000);
    return new Date() >= cutoffTime;
  };

  const isWithinCutoffCoach = (prog) => {
    if (!prog.date || !prog.startTime) return false;
    const [h, m] = prog.startTime.split(':').map(Number);
    const programTime = new Date(prog.date + 'T00:00:00');
    programTime.setHours(h, m, 0, 0);
    const cutoffTime = new Date(programTime.getTime() - coachMeta.cutoffHours * 60 * 60 * 1000);
    return new Date() >= cutoffTime;
  };

  const isWithinCutoffCourt = (assignment) => {
    if (!assignment.date) return false;
    let assignmentTime;
    if (assignment.startTime) {
      const [h, m] = assignment.startTime.split(':').map(Number);
      assignmentTime = new Date(assignment.date + 'T00:00:00');
      assignmentTime.setHours(h, m, 0, 0);
    } else {
      assignmentTime = new Date(assignment.date + 'T00:00:00');
    }
    const cutoffTime = new Date(assignmentTime.getTime() - courtMeta.cutoffHours * 60 * 60 * 1000);
    return new Date() >= cutoffTime;
  };

  const canRespondToItem = (item) => {
    if (item.isLocked || item.isPast) return false;
    if (item._type === 'game') return !isWithinCutoffGame(item);
    if (item._type === 'coach') return !isWithinCutoffCoach(item);
    if (item._type === 'court') return !isWithinCutoffCourt(item);
    return false;
  };

  const isResponseEnabled = (item) => {
    if (item._type === 'game') return gameMeta.responseEnabled;
    if (item._type === 'coach') return coachMeta.responseEnabled;
    if (item._type === 'court') return courtMeta.responseEnabled;
    return false;
  };

  // Opens coach mode for a trial day in a new tab (same as the Coach tab).
  const handleLaunchTrial = async (trial) => {
    const tab = openBlankCoachTab();
    setLaunchingId(trial.subEventId);
    setError('');
    try {
      const res = await publicFetch(`${API_BASE}/public/swishiq/portal/${token}/unlock`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subEventId: trial.subEventId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Couldn't open coach mode (HTTP ${res.status})`);
      openCoachMode(data, tab);
    } catch (e) {
      if (tab) tab.close();
      setError(e.message);
    } finally {
      setLaunchingId(null);
    }
  };

  // ==================== RESPONSE HANDLERS ====================

  const handleGameRespond = async (game, response) => {
    if (response === 'rejected') {
      if (!window.confirm('Are you sure you want to reject this shift?')) return;
    }
    try {
      const res = await publicFetch(`${API_BASE}/public/roster/games/${game.gameId}/respond/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ response })
      });
      if (res.ok) {
        setGames(prev => prev.map(g => g.gameId === game.gameId ? { ...g, response } : g));
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to submit response');
      }
    } catch (err) {
      alert('Error submitting response: ' + err.message);
    }
  };

  const handleCoachRespond = async (prog, response) => {
    if (response === 'rejected') {
      if (!window.confirm('Are you sure you want to reject this shift?')) return;
    }
    try {
      const res = await publicFetch(`${API_BASE}/public/roster/coaching/${prog.assignmentId}/respond/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ response })
      });
      if (res.ok) {
        setPrograms(prev => prev.map(p => p.assignmentId === prog.assignmentId ? { ...p, response } : p));
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to submit response');
      }
    } catch (err) {
      alert('Error submitting response: ' + err.message);
    }
  };

  const handleCourtRespond = async (assignment, response) => {
    if (response === 'rejected') {
      if (!window.confirm('Are you sure you want to reject this shift?')) return;
    }
    try {
      const res = await publicFetch(`${API_BASE}/public/roster/court/${assignment.id}/respond/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ response })
      });
      if (res.ok) {
        setCourtAssignments(prev => prev.map(a => a.id === assignment.id ? { ...a, response } : a));
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to submit response');
      }
    } catch (err) {
      alert('Error submitting response: ' + err.message);
    }
  };

  const handleRespond = (item, response) => {
    if (item._type === 'game') return handleGameRespond(item, response);
    if (item._type === 'coach') return handleCoachRespond(item, response);
    if (item._type === 'court') return handleCourtRespond(item, response);
  };

  // ==================== ICS EXPORT ====================

  const handleExportICS = () => {
    const icsItems = [];

    games.filter(g => g.response !== 'rejected').forEach(g => {
      icsItems.push({
        type: 'game',
        date: g.date,
        startTime: g.time,
        endTime: null,
        durationMinutes: 60,
        summary: `Game - ${g.team1} vs ${g.team2}`,
        location: g.court || null,
        description: `Division: ${g.division || 'N/A'}\nRole: ${g.role || 'N/A'}${payVisible ? `\nPay: $${(g.pay || 0).toFixed(2)}` : ''}`,
        uid: `game-${g.gameId}@boms-roster`
      });
    });

    programs.filter(p => p.response !== 'rejected').forEach(p => {
      icsItems.push({
        type: 'coach',
        date: p.date,
        startTime: p.startTime,
        endTime: null,
        durationMinutes: p.durationMinutes || 60,
        summary: `Coaching - ${p.programType}`,
        location: p.location || null,
        description: `Role: ${p.qualification || 'Coach'}\nDuration: ${p.durationMinutes} min${payVisible ? `\nPay: $${(p.pay || 0).toFixed(2)}` : ''}`,
        uid: `coach-${p.assignmentId}@boms-roster`
      });
    });

    courtAssignments.filter(a => a.response !== 'rejected').forEach(a => {
      icsItems.push({
        type: 'court',
        date: a.date,
        startTime: a.startTime || null,
        endTime: a.endTime || null,
        durationMinutes: null,
        summary: `Court Duty - ${a.qualification || 'Court Official'}`,
        location: null,
        description: `Role: ${a.qualification || 'Court Official'}${payVisible ? `\nPay: $${(a.pay || 0).toFixed(2)}` : ''}`,
        uid: `court-${a.id}@boms-roster`
      });
    });

    trials.forEach(s => {
      icsItems.push({
        type: 'trial',
        date: s.date,
        startTime: s.startTime || null,
        endTime: s.endTime || null,
        durationMinutes: null,
        summary: `SwishIQ Trials - ${s.eventName}`,
        location: null,
        description: `Day ${s.dayNumber} of ${s.dayCount} · ${s.type}\nRole: ${s.isHeadCoach ? 'Head coach' : 'Assistant coach'}`,
        uid: `trial-${s.subEventId}@boms-roster`
      });
    });

    downloadICS(icsItems, 'my-roster.ics');
  };

  // ==================== BUILD UNIFIED ITEMS ====================

  const allItems = [
    ...games.map(g => ({ ...g, _type: 'game', _id: `game-${g.gameId}`, _sortKey: `${g.date}T${g.time || '00:00'}` })),
    ...programs.map(p => ({ ...p, _type: 'coach', _id: `coach-${p.assignmentId}`, _sortKey: `${p.date}T${p.startTime || '00:00'}` })),
    ...courtAssignments.map(a => ({ ...a, _type: 'court', _id: `court-${a.id}`, _sortKey: `${a.date}T${a.startTime || '00:00'}` })),
    ...trials.map(s => ({ ...s, _type: 'trial', _id: `trial-${s.subEventId}`, _sortKey: `${s.date}T${s.startTime || '00:00'}` }))
  ];
  allItems.sort((a, b) => a._sortKey.localeCompare(b._sortKey));

  const groupedByDate = {};
  allItems.forEach(item => {
    if (!groupedByDate[item.date]) groupedByDate[item.date] = [];
    groupedByDate[item.date].push(item);
  });
  const sortedDates = Object.keys(groupedByDate).sort();

  const combinedTotalPay = gameMeta.totalPay + coachMeta.totalPay + courtMeta.totalPay;
  const combinedAttendedPay = gameMeta.attendedPay + coachMeta.attendedPay + courtMeta.attendedPay;

  // ==================== RENDER HELPERS ====================

  const anyResponseEnabled = gameMeta.responseEnabled || coachMeta.responseEnabled || courtMeta.responseEnabled;

  const getRowClass = (item) => {
    if (item.response === 'rejected') return 'public-rejected-row';
    if (item.response === 'accepted') return 'public-accepted-row';
    if (item.isPast && item.isLocked && !item.attended) return 'public-unattended-row';
    return '';
  };

  const renderResponseControls = (item) => {
    if (!isResponseEnabled(item)) return null;

    if (item.response === 'accepted') {
      return <span className="public-response-badge accepted">Accepted</span>;
    }
    if (item.response === 'rejected') {
      return <span className="public-response-badge rejected">Rejected</span>;
    }

    const canResp = canRespondToItem(item);
    if (!canResp) return null;
    return (
      <div className="public-response-buttons">
        <button
          className="public-btn-accept"
          onClick={() => handleRespond(item, 'accepted')}
          title="Accept this shift"
        >&#10003;</button>
        <button
          className="public-btn-reject"
          onClick={() => handleRespond(item, 'rejected')}
          title="Reject this shift"
        >&#10007;</button>
      </div>
    );
  };

  const renderStatusBadge = (item) => {
    if (item._type === 'trial') {
      if (item.canLaunchNow) {
        return (
          <button className="public-trial-launch" onClick={() => handleLaunchTrial(item)} disabled={launchingId === item.subEventId}>
            {launchingId === item.subEventId ? 'Opening…' : 'Launch coach mode'}
          </button>
        );
      }
      return new Date(item.closesAt) < new Date()
        ? <span className="public-status-badge attended">Finished</span>
        : null;
    }
    const isUnattended = item.isPast && item.isLocked && !item.attended;
    const statusBadge = isUnattended
      ? <span className="public-status-badge unattended">Unattended</span>
      : item.attended
        ? <span className="public-status-badge attended">Attended</span>
        : null;

    const notesBtn = (item._type === 'coach' && item.notes) ? (
      <button
        className="public-notes-link"
        onClick={() => {
          const w = window.open('', '_blank');
          w.document.write(`<!DOCTYPE html><html><head><title>Notes — ${item.programType}</title><style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;max-width:700px;margin:2rem auto;padding:1rem;color:#333;line-height:1.6;}h2{color:#2e7d32;border-bottom:2px solid #e0e0e0;padding-bottom:0.5rem;}.meta{color:#888;font-size:0.9rem;margin-bottom:1rem;}.notes{white-space:pre-wrap;background:#f8f9fa;padding:1rem;border-radius:8px;border:1px solid #e0e0e0;}</style></head><body><h2>${item.programType}</h2><div class="meta">${formatLongDate(item.date)} &bull; ${formatTime(item.startTime)} &bull; ${item.durationMinutes} min</div><div class="notes">${item.notes.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</div></body></html>`);
          w.document.close();
        }}
        title="View notes"
      >
        Notes
      </button>
    ) : null;

    if (!statusBadge && !notesBtn) return null;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
        {statusBadge}
        {notesBtn}
      </span>
    );
  };

  const getItemName = (item) => {
    if (item._type === 'game') return <>{item.team1} vs {item.team2}</>;
    if (item._type === 'coach') {
      return item.programType;
    }
    if (item._type === 'court') return item.qualification || 'Court Official';
    if (item._type === 'trial') return item.eventName;
    return '';
  };

  const getItemLocation = (item) => {
    if (item._type === 'game') {
      const parts = [];
      if (item.division) parts.push(item.division);
      if (item.court) parts.push(item.court);
      return parts.join(' · ') || '-';
    }
    if (item._type === 'coach') {
      const parts = [];
      if (item.location) parts.push(item.location);
      if (item.durationMinutes) parts.push(`${item.durationMinutes} min`);
      return parts.join(' · ') || '-';
    }
    if (item._type === 'court') {
      return item.qualificationDescription || '-';
    }
    if (item._type === 'trial') {
      return `Day ${item.dayNumber} of ${item.dayCount} · ${item.type} · ${item.playerCount} players`;
    }
    return '-';
  };

  // Add `dur` minutes to a "HH:MM" time string and return "HH:MM".
  const addMinutesToHHMM = (hhmm, dur) => {
    if (!hhmm || !dur) return null;
    const [h, m] = hhmm.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    const total = h * 60 + m + dur;
    const eh = Math.floor(total / 60) % 24;
    const em = total % 60;
    return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
  };

  const getItemTime = (item) => {
    if (item._type === 'game') return formatTime(item.time);
    if (item._type === 'coach') {
      const end = addMinutesToHHMM(item.startTime, item.durationMinutes);
      return end
        ? `${formatTime(item.startTime)} – ${formatTime(end)}`
        : formatTime(item.startTime);
    }
    if (item._type === 'court' || item._type === 'trial') {
      if (!item.startTime) return '-';
      return item.endTime ? `${formatTime(item.startTime)} – ${formatTime(item.endTime)}` : formatTime(item.startTime);
    }
    return '-';
  };

  const getItemRole = (item) => {
    if (item._type === 'game') {
      // Referee slot's role chip (CC / R / T / admin-defined) — read-only here;
      // editable only on Game Planner. Comes from the per-slot refereeCode set
      // by the backend (defaults to the code at displayOrder = slotOrder, can
      // be cycled by an admin from Game Planner).
      const slotChip = item.role === 'Referee' ? (item.refereeCode || '') : '';
      return (
        <span className={`public-role-badge ${item.role === 'Referee' ? 'referee' : 'bench-official'}`}>
          {slotChip ? `${slotChip} ${item.role}` : item.role}
        </span>
      );
    }
    if (item._type === 'coach') {
      // Coach code chip (Admin → Coach Codes), set on the Coach Planner.
      const label = [item.coachCode, item.qualification].filter(Boolean).join(' ');
      return label ? <span className="public-role-badge referee" title={item.coachCodeLabel || ''}>{label}</span> : null;
    }
    if (item._type === 'court') {
      return <span className="public-role-badge court-official">Court Official</span>;
    }
    if (item._type === 'trial') {
      return <span className="public-role-badge referee">{item.isHeadCoach ? 'Head coach' : 'Assistant coach'}</span>;
    }
    return null;
  };

  const TYPE_BADGE = {
    game: { cls: 'public-type-game', label: 'Game' },
    coach: { cls: 'public-type-coach', label: 'Coaching' },
    court: { cls: 'public-type-court', label: 'Court' },
    trial: { cls: 'public-type-trial', label: 'SwishIQ Trials' },
  };

  // ==================== MAIN RENDER ====================

  return (
    <div>
      {error && <div className="public-tab-error">{error}</div>}

      {loading ? (
        <div className="public-tab-loading">Loading roster...</div>
      ) : allItems.length === 0 ? (
        <div className="public-empty-state">
          <p>No assignments for this period</p>
        </div>
      ) : (
        <>
          <div className="public-roster-table-scroll">
            <table className="public-roster-table public-my-roster-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Time</th>
                  <th>Name</th>
                  <th>Location / Details</th>
                  <th>Role</th>
                  {payVisible && <th>Pay</th>}
                  <th>Status</th>
                  {anyResponseEnabled && <th>Response</th>}
                </tr>
              </thead>
              <tbody>
                {sortedDates.map(date => (
                  <React.Fragment key={date}>
                    <tr className="public-date-group-header">
                      <td colSpan={(payVisible ? 1 : 0) + (anyResponseEnabled ? 1 : 0) + 6}>{formatLongDate(date)}</td>
                    </tr>
                    {groupedByDate[date].map(item => (
                      <React.Fragment key={item._id}>
                        <tr className={getRowClass(item)}>
                          <td data-label="Type">
                            <span className={`public-type-badge ${TYPE_BADGE[item._type].cls}`}>
                              {TYPE_BADGE[item._type].label}
                            </span>
                          </td>
                          <td data-label="Time" style={{ whiteSpace: 'nowrap' }}>{getItemTime(item)}</td>
                          <td data-label="Name">{getItemName(item)}</td>
                          <td data-label="Location">{getItemLocation(item)}</td>
                          <td data-label="Role">{getItemRole(item)}</td>
                          {payVisible && <td data-label="Pay" className="public-pay-cell">{item._type === 'trial' ? '–' : `$${(item.pay || 0).toFixed(2)}`}</td>}
                          <td data-label="Status">{renderStatusBadge(item)}</td>
                          {anyResponseEnabled && <td data-label="Response">{renderResponseControls(item)}</td>}
                        </tr>
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {payVisible && (
            <div className="public-pay-summary">
              <div className="public-pay-item">
                <span>Forecast Pay:</span>
                <strong>${combinedTotalPay.toFixed(2)}</strong>
              </div>
              <div className="public-pay-item attended">
                <span>Attended Pay:</span>
                <strong>${combinedAttendedPay.toFixed(2)}</strong>
              </div>
            </div>
          )}

          <div className="public-my-roster-actions" style={{ justifyContent: 'center' }}>
            <button
              className="public-ics-export-btn"
              onClick={handleExportICS}
              disabled={allItems.length === 0}
              title="Download calendar file (.ics) for import into Apple Calendar, Google Calendar, Outlook, etc."
            >
              &#128197; Export to Calendar
            </button>
          </div>
        </>
      )}
    </div>
  );
}
